/**
 * lib/uiVariant.js — Phase 1 (Figma migration) rollout gate.
 *
 * Amendment #1: we deliberately do NOT introduce a new backend feature flag.
 * The existing feature-flag pipeline (`useFeatureFlags` → `/api/config/flags`)
 * is backend-authoritative, and this migration must not touch the backend.
 *
 * Instead, the shell variant is decided purely on the client:
 *
 *   1. `localStorage['2120_ui_variant']`   → power-user override.
 *   2. `window.location.hostname` matches Preview subdomain → 'new'.
 *   3. Default → 'legacy'.
 *
 * ROLLOUT & ROLLBACK PROPERTIES (Amendment #10 clarification)
 * -----------------------------------------------------------
 * There is NO server-side rollout infrastructure in this migration. The
 * three levers below have different scopes, latencies, and rollback costs:
 *
 *   A) `localStorage['2120_ui_variant']` — IMMEDIATE, PER-BROWSER.
 *      • Takes effect on the next render (or page reload).
 *      • Scope: one browser profile on one device for one user.
 *      • Use case: an engineer or power-user opting one browser in or
 *        out of the new shell for spot-checking / debugging.
 *      • Rollback: `localStorage.removeItem('2120_ui_variant')` or
 *        `setUiVariant(null)` — instant, single-browser.
 *
 *   B) Preview hostname auto-selection — AUTOMATIC, ENVIRONMENT-SCOPED.
 *      • Every visit to a `*.preview.emergentagent.com` (or localhost)
 *        origin resolves to the new shell without any override needed.
 *      • Rollback for Preview only: set an explicit
 *        `localStorage['2120_ui_variant']='legacy'` (per browser) or
 *        remove the pattern from PREVIEW_HOST_PATTERNS (deployment).
 *
 *   C) `DEFAULT_VARIANT` constant — GLOBAL DEFAULT, DEPLOYMENT-GATED.
 *      • Controls what every browser sees on non-Preview hosts (i.e.
 *        Production) when no localStorage override is present.
 *      • Changing this value is a code edit + build + deploy: it takes
 *        effect only after the frontend is redeployed with the new bundle.
 *      • Not instant. If Production regresses after a `DEFAULT_VARIANT`
 *        change, the recovery path is another deployment (or asking
 *        affected users to set the localStorage override for immediate
 *        per-browser rollback while the fix ships).
 *
 * Consumers:
 *   - `AppShell.js`         → picks NAV_GROUPS shape
 *   - `MobileMoreSheet.js`  → picks group structure
 *
 * The helper is deliberately synchronous so shell code can decide on first
 * render without any Suspense boundary or bootstrap gate.
 */

const LS_KEY = '2120_ui_variant';
// Phase 84.4 (v3.0.2) — Production default flipped to 'new'.
//
// Historically this defaulted to 'legacy' while the Figma-migration new
// shell was in progress, and preview subdomains auto-opted into 'new' via
// the hostname match below. That directive is now inverted: the migration
// is complete, new shell is authoritative on both preview and production,
// and 'legacy' remains only as an escape hatch via `localStorage`.
//
// Recovery lever if the new shell regresses on prod:
//   • Per-browser rollback: set `localStorage['2120_ui_variant']='legacy'`
//   • Fleet-wide rollback:  ship a deploy that flips this constant back
const DEFAULT_VARIANT = 'new';

// Hosts that should default to the new shell without needing an opt-in.
// Everything on the Emergent preview subdomain is treated as an
// integration environment where the new shell is expected to be visible.
const PREVIEW_HOST_PATTERNS = [
  /\.preview\.emergentagent\.com$/i,
  /^localhost$/i,
  /^127\.0\.0\.1$/,
];

function readOverride() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const v = window.localStorage.getItem(LS_KEY);
    if (v === 'new' || v === 'legacy') return v;
    return null;
  } catch {
    return null;
  }
}

function hostMatchesPreview() {
  try {
    if (typeof window === 'undefined' || !window.location) return false;
    const host = window.location.hostname || '';
    return PREVIEW_HOST_PATTERNS.some((rx) => rx.test(host));
  } catch {
    return false;
  }
}

/**
 * Resolve the shell variant.
 *
 * Returns `'new'` | `'legacy'`.
 */
export function getUiVariant() {
  const override = readOverride();
  if (override) return override;
  if (hostMatchesPreview()) return 'new';
  return DEFAULT_VARIANT;
}

/**
 * Persist a manual override (useful from DevTools or a hidden settings row).
 *   setUiVariant('new')     → force the new shell everywhere
 *   setUiVariant('legacy')  → force the old shell
 *   setUiVariant(null)      → clear the override; fall back to defaults
 */
export function setUiVariant(variant) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (variant === 'new' || variant === 'legacy') {
      window.localStorage.setItem(LS_KEY, variant);
    } else {
      window.localStorage.removeItem(LS_KEY);
    }
  } catch {
    /* localStorage unavailable — silently ignore. */
  }
}

/** Convenience predicate for boolean call sites. */
export function isNewShell() {
  return getUiVariant() === 'new';
}

export const UI_VARIANT_STORAGE_KEY = LS_KEY;
